import { AUTH_STORAGE_KEY } from '@src/features/auth';
import { de, enUS, type Locale } from 'date-fns/locale';

export type AppLanguage = 'en' | 'de';

type AppLanguageConfig = {
  labelKey: string;
  labelFallback: string;
  backendCode: string;
  intlCode: string;
  dateLocale: Locale;
};

export const APP_LANGUAGES: Record<AppLanguage, AppLanguageConfig> = {
  en: {
    labelKey: 'settings.language.english',
    labelFallback: 'English',
    backendCode: 'en',
    intlCode: 'en-US',
    dateLocale: enUS,
  },
  de: {
    labelKey: 'settings.language.german',
    labelFallback: 'German',
    backendCode: 'de',
    intlCode: 'de-DE',
    dateLocale: de,
  },
};

export const APP_LANGUAGE_OPTIONS: Array<{
  code: AppLanguage;
  config: AppLanguageConfig;
}> = [
  { code: 'en', config: APP_LANGUAGES.en },
  { code: 'de', config: APP_LANGUAGES.de },
];

export const DEFAULT_APP_LANGUAGE: AppLanguage = 'en';

function getBackendLanguageForAuthUser(): string {
  let language = APP_LANGUAGES[DEFAULT_APP_LANGUAGE].backendCode;
  const value = localStorage.getItem(AUTH_STORAGE_KEY);
  if (value) {
    const authData = JSON.parse(value);
    if (authData) {
      const userLanguage = authData.user?.language;
      if (userLanguage) {
        language = userLanguage;
      }
    }
  }
  return language;
}

export function mapBackendLanguageToApp(
  language: string | null | undefined,
): AppLanguage {
  const normalized = String(language ?? '').toLowerCase();
  if (normalized.startsWith('de')) {
    return 'de';
  }
  return 'en';
}

export function mapAppLanguageToBackend(language: AppLanguage): string {
  return APP_LANGUAGES[language].backendCode;
}

export function getInitialAppLanguage(): AppLanguage {
  return mapBackendLanguageToApp(getBackendLanguageForAuthUser());
}

export function getIntlLocaleTag(language = getInitialAppLanguage()): string {
  return APP_LANGUAGES[language].intlCode;
}

export function getDateAdapterLocale(
  language = getInitialAppLanguage(),
): Locale {
  return APP_LANGUAGES[language].dateLocale || enUS;
}
